import type { FC } from "react";
import type { CheckInPermissions } from "../../api/dto/ProjectCheckIn";
import type { QuestionSetType } from "../../api/dto/QuestionSet";
import { Callout } from "../content/Callout";
import { CheckBox } from "../form/CheckBox";
import { Input } from "../form/Input";
import { checkInPermissionOptions, checkInQuestionSetOptions } from "./checkInPermissionLabels";

export const defaultGrantHours = 48;
export const maxGrantHours = 168;

export const defaultCheckInPermissions: CheckInPermissions = {
  canExecuteAssessments: true,
  canExportAssessments: false,
  canAccessAllAssessments: false,
  canAccessAssessmentHistory: false,
  canManageProjects: false,
  canManageAssessments: false,
  canManageAssessmentsDestructive: false,
  accessibleQuestionSetTypes: checkInQuestionSetOptions.map(([type]) => type),
};

export const isValidGrantHours = (hours: number) =>
  Number.isInteger(hours) && hours >= 1 && hours <= maxGrantHours;

const CheckRow: FC<{ label: string; checked: boolean; onChange: (checked: boolean) => void }> = (
  props,
) => (
  <div className={"flex items-center text-sm"}>
    <CheckBox
      state={props.checked ? "checked" : "unchecked"}
      onSelect={() => props.onChange(true)}
      onDeselect={() => props.onChange(false)}
    />
    <span className={"ml-1"}>{props.label}</span>
  </div>
);

/** The part of the start/edit check-in modals that decides what visitors get. */
export const CheckInSettingsFields: FC<{
  grantHours: number;
  onGrantHoursChange: (hours: number) => void;
  permissions: CheckInPermissions;
  onPermissionsChange: (permissions: CheckInPermissions) => void;
  errors: string[];
}> = (props) => {
  const toggleQuestionSet = (type: QuestionSetType, checked: boolean) =>
    props.onPermissionsChange({
      ...props.permissions,
      accessibleQuestionSetTypes: checked
        ? [...props.permissions.accessibleQuestionSetTypes, type]
        : props.permissions.accessibleQuestionSetTypes.filter((t) => t !== type),
    });

  return (
    <>
      <Input
        type={"number"}
        label={`Toegang geldig voor (uren, maximaal ${maxGrantHours})`}
        value={props.grantHours}
        onChange={props.onGrantHoursChange}
        maxWidth={"sm"}
      />
      <div>
        <div className={"text-sm font-medium text-brand-900"}>Rechten</div>
        {checkInPermissionOptions.map(([flag, label]) => (
          <CheckRow
            key={flag}
            label={label}
            checked={props.permissions[flag]}
            onChange={(checked) =>
              props.onPermissionsChange({ ...props.permissions, [flag]: checked })
            }
          />
        ))}
      </div>
      <div>
        <div className={"text-sm font-medium text-brand-900"}>Toegankelijke vragenlijsten</div>
        {checkInQuestionSetOptions.map(([type, label]) => (
          <CheckRow
            key={type}
            label={label}
            checked={props.permissions.accessibleQuestionSetTypes.includes(type)}
            onChange={(checked) => toggleQuestionSet(type, checked)}
          />
        ))}
      </div>
      {props.errors.length > 0 && (
        <Callout color={"danger"}>
          {props.errors.map((error) => (
            <div key={error}>{error}</div>
          ))}
        </Callout>
      )}
    </>
  );
};
